/**
 * Cycle Builder Wizard – Step 2: Assessment Event Scheduling
 */
import React, { useMemo } from "react";
import { translateUi } from '../i18n/index';
import { Plus, Trash2 } from "../icons";
import { useCycleWizard, type WizardAssessmentEvent } from "./CycleWizardStore";
import { countWizardCycleStats } from "./cycleLibraryUi";
import { CompanyCycleWorkflowCallout } from "./CompanyCycleWorkflowCallout";
import { cb, cbCn } from "./cycleBuilderTheme";

export function CycleWizardStep2({
  onBack,
  onSave,
  saving,
  isMobile,
  isDarkMode = false,
  companyCode,
  editingExistingCycle = false,
}: {
  onBack: () => void;
  onSave: () => void;
  saving: boolean;
  currentUserEmail?: string | null;
  companyCode?: string;
  isMobile?: boolean;
  isDarkMode?: boolean;
  editingExistingCycle?: boolean;
}) {
  const { state, dispatch, durationInMonths } = useCycleWizard();
  const companyWizardContext = Boolean(companyCode?.trim());
  const criteriaStats = useMemo(() => countWizardCycleStats(state.criteriaItems), [state.criteriaItems]);
  const inputClass = cbCn(
    "mt-1 w-full rounded-lg border px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500",
    cb.input(isDarkMode)
  );

  const addEvent = () => {
    const lastOrder = state.assessmentEvents.length > 0 ? Math.max(...state.assessmentEvents.map((e) => e.sort_order)) : -1;
    const prev = state.assessmentEvents.length > 0 ? state.assessmentEvents[state.assessmentEvents.length - 1] : null;
    const startFromPrev = prev
      ? Math.min(durationInMonths, Math.max(0, Number(prev.offset_months_close) || 0))
      : 0;
    const endDefault = prev
      ? startFromPrev
      : Math.min(1, durationInMonths);
    dispatch({
      type: "ADD_EVENT",
      event: {
        event_name: "",
        description: "",
        offset_months_open: startFromPrev,
        offset_months_close: endDefault,
        sort_order: lastOrder + 1,
      },
    });
  };

  const updateEvent = (index: number, field: keyof WizardAssessmentEvent, value: string | number) => {
    dispatch({ type: "UPDATE_EVENT", index, event: { [field]: value } });
  };

  const removeEvent = (index: number) => {
    dispatch({ type: "REMOVE_EVENT", index });
  };

  const eventErrors = useMemo(() => {
    const errors: Record<number, string> = {};
    state.assessmentEvents.forEach((ev, i) => {
      const open = Number(ev.offset_months_open);
      const close = Number(ev.offset_months_close);
      if (!ev.event_name?.trim()) errors[i] = "Title is required";
      else if (!Number.isFinite(open) || open < 0 || open > durationInMonths)
        errors[i] = `Start month must be between 0 and ${durationInMonths}`;
      else if (!Number.isFinite(close) || close < 0 || close > durationInMonths)
        errors[i] = `End month must be between 0 and ${durationInMonths}`;
      else if (close < open) errors[i] = "End month must be >= start month";
    });
    return errors;
  }, [state.assessmentEvents, durationInMonths]);

  const allEventsValid = state.assessmentEvents.length > 0 && Object.keys(eventErrors).length === 0;
  const canSave = editingExistingCycle || allEventsValid;

  const monthOptions = useMemo(() => {
    const opts: number[] = [];
    for (let m = 0; m <= durationInMonths; m++) opts.push(m);
    return opts;
  }, [durationInMonths]);

  const monthLabel = (m: number) =>
    m === 0 ? "Month 0 (from start)" : `Month ${m}`;

  return (
    <div className="space-y-4">
      {companyWizardContext && state.selectedFrameworkTemplateId ? (
        <CompanyCycleWorkflowCallout
          activeStep="events"
          isDarkMode={isDarkMode}
          cycleTitle={state.title}
          criteriaCount={criteriaStats.criteria}
        />
      ) : null}
      <section className={cb.stageCard(isDarkMode)}>
        <h3 className={cb.heading(isDarkMode, true)}>{translateUi("Schedule assessment events")}</h3>
        <p className={cbCn("mt-1 text-sm", cb.body(isDarkMode))}>
          {companyWizardContext
            ? translateUi(
                "Set when each assessment opens and closes within the {{months}}-month cycle. Then save to create your company cycle as a draft.",
                { months: durationInMonths }
              )
            : translateUi(
                "Add each assessment window within the {{months}}-month cycle. You need at least one event to save. Month 0 is the assignment start date (today when assigned). Same start and end month is a one-month window (e.g. 0→0 is from start through the first month). Continuous renewal uses schedule order: every assessment event must be completed (including the last in the schedule) before a continuous cycle can renew.",
                { months: durationInMonths }
              )}
        </p>

        <div className="mt-5 space-y-4">
          {state.assessmentEvents.map((ev, i) => (
            <div
              key={i}
              className={cbCn(
                "rounded-lg border p-4",
                eventErrors[i]
                  ? isDarkMode
                    ? "border-amber-500/40 bg-amber-950/30"
                    : "border-amber-300 bg-amber-50/50"
                  : isDarkMode
                    ? "border-slate-600 bg-slate-800/50"
                    : "border-slate-200 bg-slate-50/30"
              )}
            >
              <div className={cbCn("flex items-start gap-4", isMobile ? "flex-col" : "flex-wrap")}>
                <div className={cbCn("flex-1", isMobile ? "w-full" : "min-w-[200px]")}>
                  <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}> {translateUi("Title")} </label>
                  <input
                    type="text"
                    value={ev.event_name}
                    onChange={(e) => updateEvent(i, "event_name", e.target.value)}
                    placeholder="e.g. Practical Ride Assessment"
                    className={inputClass}
                  />
                </div>
                <div className={isMobile ? "w-full" : "min-w-[160px]"}>
                  <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Start month")}</label>
                  <select
                    value={ev.offset_months_open}
                    onChange={(e) => updateEvent(i, "offset_months_open", Number(e.target.value))}
                    className={inputClass}
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {monthLabel(m)}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={isMobile ? "w-full" : "min-w-[160px]"}>
                  <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("End month")}</label>
                  <select
                    value={ev.offset_months_close}
                    onChange={(e) => updateEvent(i, "offset_months_close", Number(e.target.value))}
                    className={inputClass}
                  >
                    {monthOptions.map((m) => (
                      <option key={m} value={m}>
                        {monthLabel(m)}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => removeEvent(i)}
                  className={cbCn(
                    "rounded",
                    isMobile ? "mt-4 p-3 min-w-[44px] min-h-[44px]" : "mt-6 p-2",
                    isDarkMode
                      ? "text-slate-400 hover:bg-red-950/50 hover:text-red-300"
                      : "text-slate-500 hover:bg-red-50 hover:text-red-600"
                  )}
                  title={translateUi("Remove event")}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-3">
                <label className={cbCn("block text-sm font-medium", cb.label(isDarkMode))}>{translateUi("Description (optional)")}</label>
                <input
                  type="text"
                  value={ev.description ?? ""}
                  onChange={(e) => updateEvent(i, "description", e.target.value)}
                  placeholder="Optional description"
                  className={inputClass}
                />
              </div>
              {eventErrors[i] && <p className={cbCn("mt-2 text-sm", cb.hint(isDarkMode))}>{eventErrors[i]}</p>}
            </div>
          ))}
        </div>
      </section>

      <div className={cbCn("flex gap-3", isMobile ? "flex-col" : "items-center justify-between")}>
        <button
          type="button"
          onClick={onBack}
          className={cbCn(
            "rounded-xl font-medium",
            isMobile ? "w-full py-3 min-h-[48px] text-base order-3" : "px-6 py-2.5",
            cb.btnSecondary(isDarkMode)
          )}
        > {translateUi("Back")} </button>
        <div className={cbCn("flex gap-3", isMobile ? "w-full flex-col order-1" : "items-center")}>
          <button
            type="button"
            onClick={addEvent}
            className={cbCn(
              "inline-flex items-center justify-center gap-2 rounded-xl font-semibold border-2 transition-colors",
              isMobile ? "w-full py-3 min-h-[48px] text-base" : "px-6 py-2.5",
              isDarkMode
                ? "border-indigo-400 bg-indigo-950/50 text-indigo-200 hover:bg-indigo-900/70"
                : "border-indigo-500 bg-indigo-50 text-indigo-800 hover:bg-indigo-100"
            )}
          >
            <Plus className="h-4 w-4" />
            {translateUi("Add event")}
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave || saving}
            className={cbCn(
              "rounded-xl font-medium transition-colors",
              isMobile ? "w-full py-3 min-h-[48px] text-base" : "px-6 py-2.5",
              cb.btnPrimary(!canSave || saving)
            )}
          >
            {saving
              ? translateUi("Saving…")
              : companyWizardContext
                ? editingExistingCycle
                  ? translateUi("Update company cycle")
                  : translateUi("Save company cycle")
                : translateUi("Save cycle")}
          </button>
        </div>
      </div>
    </div>
  );
}
